import React, { Component } from "react";
import yup from "yup";
import { Date, RadioGroup } from "@common/formx";
import { Remove } from "@material-ui/icons";
import locale from "@bonliva-crm/locale";
import { Text } from "@common/formx_item_form";
import { Grid, IconButton, Divider, Typography } from "@material-ui/core";
import { ConsultantContractBonus, ConsultantContractBonusType, Currency } from "@graphql";
import { moment } from "@bonliva-crm/common";
import {
  calculatePriceOverSalaryLimit,
  calculatePriceUnderSalaryLimit
} from "@consultants/ConsultantContract/utils";

type Props = {
  index: number;
  removeBonus: () => void;
  data: ConsultantContractBonus;
  withSalaryType: boolean;
  isNationalProcurementPriceStrategy?: boolean;
};

export default class WorkedHourBonus extends Component<Props> {
  public static BlankState(): ConsultantContractBonus {
    return {
      _id: null,
      Type: "WorkedHourBonus",
      SalaryType: ConsultantContractBonusType.GrossSalaryBonus,
      Config: {
        StartDate: null,
        EndDate: null
      },
      Amount: 0,
      Currency: Currency.SEK
    };
  }

  public static ValidationSchema(withSalaryType: boolean) {
    return yup.object().shape({
      Type: yup.string(),
      ...(withSalaryType && {
        SalaryType: yup
          .mixed()
          .oneOf(
            Object.values(ConsultantContractBonusType),
            locale.fieldIsMandatory(locale.Type)
          )
          .required(locale.fieldIsMandatory(locale.Type))
      }),
      Amount: yup
        .number()
        .positive()
        .typeError(locale.fieldIsMandatory(locale.BonusAmount))
        .moreThan(0, locale.fieldIsMandatory(locale.BonusAmount))
        .nullable(false)
        .required(locale.fieldIsMandatory(locale.BonusAmount)),
      Config: yup.object().shape({
        StartDate: yup
          .string()
          .nullable(true)
          .test(
            "StartDate",
            locale.fieldIsMandatory(locale.StartDate),
            function testStartDate(e) {
              const { EndDate: s } = this.parent;
              if (s && s != "" && !e) {
                return false;
              }
              return true;
            }
          )
          .test("StartDate", locale.NotInContractInterval, function testStartDate(e) {
            if (!e) return true;
            return moment(e).isBetween(
              (this.options.context as any).StartDate,
              (this.options.context as any).EndDate,
              "days",
              "[]"
            );
          }),
        EndDate: yup
          .string()
          .nullable(true)
          .test("EndDate", locale.EndDateAfterStartDate, function testStartDate(e) {
            const { StartDate: s } = this.parent;
            if (s && e) {
              return moment(e).isSameOrAfter(moment(s));
            }
            return true;
          })
          .test(
            "EndDate",
            locale.fieldIsMandatory(locale.EndDate),
            function testStartDate(e) {
              const { StartDate: s } = this.parent;
              if (s && s != "" && !e) {
                return false;
              }
              return true;
            }
          )
          .test("EndDate", locale.NotInContractInterval, function testStartDate(e) {
            if (!e) return true;
            return moment(e).isBetween(
              (this.options.context as any).StartDate,
              (this.options.context as any).EndDate,
              "days",
              "[]"
            );
          })
      })
    });
  }

  render() {
    const {
      removeBonus,
      index,
      data,
      withSalaryType,
      isNationalProcurementPriceStrategy
    } = this.props;

    return (
      <Grid key={index} item xs={12} container spacing={1}>
        <Grid
          item
          xs={12}
          justifyContent={"space-between"}
          wrap={"nowrap"}
          style={{ display: "flex" }}
        >
          <Typography variant="subtitle1" gutterBottom>
            {index + 1}. {locale.bonuses["WorkedHourBonus"]}
          </Typography>
          <IconButton aria-label="delete" onClick={() => removeBonus()}>
            <Remove fontSize="small" />
          </IconButton>
        </Grid>
        <Grid item xs={12}>
          <Typography variant={"body1"}>
            {`${data.Amount ?? 0} kr kommer betalas ut i bonus per arbetad timme`}
          </Typography>
        </Grid>
        <Grid item xs={3}>
          <Text
            name={`Bonus[${index}].Amount`}
            label={locale.BonusAmount}
            TextFieldProps={{
              type: "number"
            }}
          />
        </Grid>
        {isNationalProcurementPriceStrategy && (
          <Grid item xs={2} style={{ minWidth: "220px" }}>
            <div>
              {locale.BonusAmount + " <7,5: "}
              <b>{calculatePriceUnderSalaryLimit(data.Amount ?? 0, "kr")}</b>
            </div>
            <div>
              {locale.BonusAmount + " >7,5: "}
              <b>{calculatePriceOverSalaryLimit(data.Amount ?? 0, "kr")}</b>
            </div>
          </Grid>
        )}
        <Grid item container xs={12} spacing={1}>
          <Grid item xs={12}>
            <Typography variant={"body1"}>
              Start- och slutdatum sätter du enbart om bonuskriterierna endast ska gälla
              under en begränsad period i avtalsperioden. <br /> Exempelvis om bonusen
              bara ska gälla under vissa veckor under sommaren.
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Date
              utc
              key={"StartDate"}
              DatePickerProps={{
                shouldCloseOnSelect: true
              }}
              name={`Bonus[${index}].Config.StartDate`}
              label={locale.StartDate}
            />
          </Grid>
          <Grid item xs={3}>
            <Date
              utc
              key={"EndDate"}
              DatePickerProps={{
                shouldCloseOnSelect: true
              }}
              name={`Bonus[${index}].Config.EndDate`}
              label={locale.EndDate}
            />
          </Grid>
        </Grid>
        {withSalaryType && (
          <Grid item xs={12}>
            <RadioGroup
              disabled
              name={`Bonus[${index}].SalaryType`}
              ControlProps={{ fullWidth: true }}
              options={Object.values(ConsultantContractBonusType).map((v) => ({
                label: locale.consultantContractBonusType[v],
                value: v
              }))}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <Divider style={{ margin: "5px 0" }} />
        </Grid>
      </Grid>
    );
  }
}
